import useAuthStore from 'src/auth/store/firebase/auth-store';

import { useOfflineModeEnabled, getCurrentOfflineModeEnabled } from './offline-mode-access';

export type OfflineShellBlockReason =
  | 'none'
  | 'business-disabled'
  | 'auth-not-ready'
  | 'identity-unverified';

/**
 * Access V2 controls the feature; a proven local auth identity is the mandatory
 * baseline needed to expose tenant-scoped offline data.
 */
export function useOfflineShellAvailable(): boolean {
  const offlineModeEnabled = useOfflineModeEnabled();
  const authEligible = useAuthStore(
    (state) =>
      state.authRestorationStatus === 'AUTHENTICATED' &&
      state.authenticated &&
      Boolean(state.user) &&
      state.offlineIdentityVerified
  );
  return offlineModeEnabled && authEligible;
}

/** Why the offline shell is unavailable — drives retry and honest refusal copy. */
export function getCurrentOfflineShellBlockReason(): OfflineShellBlockReason {
  if (!getCurrentOfflineModeEnabled()) return 'business-disabled';
  const authState = useAuthStore.getState();
  if (
    authState.authRestorationStatus !== 'AUTHENTICATED' ||
    !authState.authenticated ||
    !authState.user
  ) {
    return 'auth-not-ready';
  }
  if (!authState.offlineIdentityVerified) return 'identity-unverified';
  return 'none';
}

export function getCurrentOfflineShellAvailable(): boolean {
  return getCurrentOfflineShellBlockReason() === 'none';
}

/** Honest refusal copy when the offline shell cannot open; shared by the V4/V5 go-offline surfaces. */
export function getOfflineShellRefusalMessage(reason: OfflineShellBlockReason): string {
  switch (reason) {
    case 'identity-unverified':
      return "This register's offline identity could not be verified. Sync the device while online, then try again.";
    case 'auth-not-ready':
      return 'Sign-in has not finished restoring on this register. Wait a moment and try again.';
    default:
      // 'business-disabled', plus the defensive 'none' race where availability
      // changed between the refusal and this read.
      return 'Offline mode is not enabled for this business.';
  }
}
